import { useState, useEffect } from 'react';
import { Loader2, Sparkles, Wand2, CalendarPlus, ListPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';

const NONE = '__none__';

const AdminAutoBlog = () => {
  const { toast } = useToast();
  const [keyword, setKeyword] = useState('');
  const [lsi, setLsi] = useState<string[]>([]);
  const [lsiLoading, setLsiLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [authors, setAuthors] = useState<{ id: string; name: string }[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [authorId, setAuthorId] = useState<string>(NONE);
  const [categoryId, setCategoryId] = useState<string>(NONE);
  const [publish, setPublish] = useState(true);
  const [promptOverride, setPromptOverride] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');

  useEffect(() => {
    (async () => {
      const [a, c, s] = await Promise.all([
        supabase.from('blog_authors').select('id, name').order('name'),
        supabase.from('blog_categories').select('id, name').order('name'),
        supabase.from('blog_settings').select('default_author_id').eq('id', 1).maybeSingle(),
      ]);
      setAuthors(a.data || []);
      setCategories(c.data || []);
      if (s.data?.default_author_id) setAuthorId(s.data.default_author_id);
    })();
  }, []);

  const toggleLsi = (k: string) =>
    setLsi(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);

  const [suggested, setSuggested] = useState<string[]>([]);

  const fetchLsi = async () => {
    if (!keyword.trim()) { toast({ title: 'Enter a keyword first', variant: 'destructive' }); return; }
    setLsiLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-lsi-keywords', { body: { keyword: keyword.trim() } });
      if (error) throw error;
      const kws: string[] = data?.keywords || [];
      setSuggested(kws);
      setLsi(kws);
    } catch (e) {
      toast({ title: 'Could not fetch keywords', description: (e as Error).message, variant: 'destructive' });
    } finally { setLsiLoading(false); }
  };

  const generateNow = async () => {
    if (!keyword.trim()) { toast({ title: 'Enter a keyword first', variant: 'destructive' }); return; }
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('autoblog-generate', {
        body: {
          keyword: keyword.trim(),
          lsi_keywords: lsi,
          author_id: authorId === NONE ? null : authorId,
          category_id: categoryId === NONE ? null : categoryId,
          prompt_override: promptOverride.trim() || null,
          source: 'manual',
          publish,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(typeof data.error === 'string' ? data.error : 'Generation failed');
      toast({ title: publish ? 'Article published' : 'Draft created', description: data.title });
      setKeyword(''); setLsi([]); setSuggested([]);
    } catch (e) {
      toast({ title: 'Generation failed', description: (e as Error).message, variant: 'destructive' });
    } finally { setGenerating(false); }
  };

  const addToQueue = async () => {
    if (!keyword.trim()) { toast({ title: 'Enter a keyword first', variant: 'destructive' }); return; }
    const { error } = await supabase.from('blog_queue').insert({
      keyword: keyword.trim(),
      lsi_keywords: lsi,
      author_id: authorId === NONE ? null : authorId,
      category_id: categoryId === NONE ? null : categoryId,
    });
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Added to queue' }); setKeyword(''); setLsi([]); setSuggested([]); }
  };

  const schedule = async () => {
    if (!keyword.trim() || !scheduledFor) {
      toast({ title: 'Keyword and date/time required', variant: 'destructive' }); return;
    }
    const { error } = await supabase.from('blog_scheduled_posts').insert({
      keyword: keyword.trim(),
      lsi_keywords: lsi,
      author_id: authorId === NONE ? null : authorId,
      category_id: categoryId === NONE ? null : categoryId,
      prompt_override: promptOverride.trim() || null,
      scheduled_for: new Date(scheduledFor).toISOString(),
    });
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' });
    else { toast({ title: 'Scheduled', description: new Date(scheduledFor).toLocaleString('en-AU') }); setKeyword(''); setLsi([]); setSuggested([]); setScheduledFor(''); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Article Generator</h1>
          <p className="text-muted-foreground mt-1">Write, queue or schedule AI articles for ACG Southern Highlands</p>
        </div>

        <Card>
          <CardHeader><CardTitle>Topic</CardTitle><CardDescription>The primary keyword the article should rank for</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input placeholder="e.g. ducted heating for frosty Bowral mornings" value={keyword} onChange={e => setKeyword(e.target.value)} />
              <Button variant="outline" onClick={fetchLsi} disabled={lsiLoading}>
                {lsiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                <span className="ml-2 hidden sm:inline">LSI keywords</span>
              </Button>
            </div>

            {suggested.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {suggested.map(k => (
                  <Badge key={k} variant={lsi.includes(k) ? 'default' : 'outline'} className="cursor-pointer" onClick={() => toggleLsi(k)}>{k}</Badge>
                ))}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Author</Label>
                <Select value={authorId} onValueChange={setAuthorId}>
                  <SelectTrigger><SelectValue placeholder="Default author" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Default author</SelectItem>
                    {authors.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger><SelectValue placeholder="No category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>No category</SelectItem>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Prompt override (optional)</Label>
              <Textarea rows={5} placeholder="Leave blank to use the saved template. Tokens: {{keyword}}, {{lsi_keywords}}, {{brand_name}}, {{domain_name}}, {{sitemap_url}}"
                value={promptOverride} onChange={e => setPromptOverride(e.target.value)} />
            </div>

            <div className="flex items-center gap-3">
              <Switch id="publish" checked={publish} onCheckedChange={setPublish} />
              <Label htmlFor="publish">Publish immediately (otherwise saved as draft)</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Actions</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button onClick={generateNow} disabled={generating}>
                {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Generate now
              </Button>
              <Button variant="outline" onClick={addToQueue}><ListPlus className="mr-2 h-4 w-4" />Add to queue</Button>
            </div>
            <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-border">
              <div className="space-y-2">
                <Label>Schedule for</Label>
                <Input type="datetime-local" value={scheduledFor} onChange={e => setScheduledFor(e.target.value)} className="w-auto" />
              </div>
              <Button variant="secondary" onClick={schedule}><CalendarPlus className="mr-2 h-4 w-4" />Schedule</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminAutoBlog;
